import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { TDS_SLUGS } from "@/lib/taxation";
import ServiceDetailTemplate from "@/pages/ServiceDetailTemplate";

const CONTACT_LABEL = "GST & Taxation";
const CATEGORY_ACCENT = { "GST Services": "emerald", "Income Tax": "gold" };
const CATEGORY_PARENT_SLUG = { "GST Services": "gst", "Income Tax": "income-tax" };

const normalize = (row) => ({
  slug: row.slug,
  category: row.category,
  subCategory: row.sub_category,
  icon: row.icon,
  image: row.image,
  title: row.title,
  subtitle: row.subtitle,
  readTime: row.read_time,
  intro: row.intro,
  ...row.sections,
});

export default function TaxationServiceDetail() {
  const { slug } = useParams();
  const { isAdmin } = useAuth();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    Promise.all([api.getService(slug), api.listServices()])
      .then(([{ service }, { services }]) => {
        setService(normalize(service));
        setAllServices(services);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (notFound) return <Navigate to="/taxation" replace />;

  const isTds = service && TDS_SLUGS.includes(service.slug);
  const parentLabel = isTds ? "TDS" : service?.category;
  const parentHref = service ? (isTds ? "/taxation/tds" : `/taxation/${CATEGORY_PARENT_SLUG[service.category] || "gst"}`) : "/taxation";
  const contactHref = `/contact?service=${encodeURIComponent(CONTACT_LABEL)}`;
  const uploadHref = service
    ? `/upload?service=${encodeURIComponent(service.slug)}&category=${encodeURIComponent(service.category)}&title=${encodeURIComponent(service.title)}`
    : "/upload";
  const relatedItems = service
    ? (service.related || [])
        .map((relSlug) => allServices.find((s) => s.slug === relSlug))
        .filter(Boolean)
        .map((r) => ({ slug: r.slug, title: r.title, icon: r.icon, href: `/taxation/${CATEGORY_PARENT_SLUG[r.category] || "gst"}/${r.slug}` }))
    : [];

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="taxation-service-page">
      <ScrollProgress />
      <Navbar />
      <main>
        {loading || !service ? (
          <div className="pt-40 pb-20 text-center text-slate-500">Loading...</div>
        ) : (
          <ServiceDetailTemplate
            service={service}
            accentKey={CATEGORY_ACCENT[service.category] || "gold"}
            breadcrumbs={[{ label: "Taxation", href: "/taxation" }, { label: parentLabel, href: parentHref }]}
            contactHref={contactHref}
            uploadHref={uploadHref}
            showUpload={!isAdmin}
            relatedItems={relatedItems}
          />
        )}
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
